interface BrandColors {
  'brand-blue': string
  'brand-pink': string
  'brand-purple': string
  'defrag-purple': string
}

export interface SurfaceColors {
  surface: string
  'surface-000': string
  'surface-100': string
  'surface-200': string
  'surface-300': string
  'surface-border-000': string
  'surface-border-100': string
  'surface-border-200': string
  'surface-border-300': string
  'surface-text-000': string
}

export interface ControlColors {
  'control-on-surface-000': string
  'control-on-surface-100': string
  'control-on-surface-200': string
  'control-on-surface-300': string
}

export interface CalendarColors {
  'calendar-event-default': string
  'calendar-event-notable': string
  'calendar-event-focus': string
  'calendar-day-border': string
  'calendar-time-border': string
  'calendar-group-footer-background': string
  'calendar-group-footer-border': string
  'calendar-time-selection-background': string
  'calendar-time-selection-border': string
}

interface NoteMentionColors {
  'note-mention-text': string
  'note-mention-border': string
  'note-widget-weekly': string
  'note-widget-adhoc': string
  'note-widget-event': string
  'note-widget-person': string
  // Mention card/tab surface + collapsed-chip fill. Derived per theme from the
  // surface scale (the light/dark split rides the other way, so the values are
  // computed in the theme rather than a .tw-dark CSS override).
  'note-mention-preview-surface': string
  'note-mention-chip-surface': string
}
interface NoteTableColors {
  'note-table-background': string
  'note-table-border': string
  'note-table-header-background': string
  'note-table-active': string
  'note-table-active-border': string
  'note-table-grip-background': string
}
interface NoteColors extends NoteMentionColors, NoteTableColors {
  'note-list-border': string
  'note-list-background': string
  // AI-generated content wash (block-level provenance highlight).
  'note-ai-highlight': string
  'note-details-border': string
  'note-details-background': string
  // Floating table toolbars (TableMenu + the row/column/cell bubble menus).
  'note-toolbar-background': string
  'note-code-background': string
  'note-code-border': string
}

export interface LayoutColors {
  'left-navigation-background': string
  'left-navigation-border': string
  'top-navigation-background': string
  'top-navigation-border': string
  'note-background': string
  'note-border': string
  'right-panel-background': string
  'right-panel-border': string

  'mobile-tabs-background-start': string
  'mobile-tabs-background-end': string
  'mobile-application-bar-background': string
  'mobile-application-bar-border': string
  'mobile-screen-background': string
  'mobile-preference-group-background': string
  'mobile-navigation-note-preview-background': string
  'mobile-navigation-note-preview-border': string
  'mobile-search-field-background': string
}

export interface RsvpColors {
  'rsvp-background': string
  'rsvp-accepted': string
  'rsvp-tentative': string
  'rsvp-rejected': string
}

export interface UiColors extends LayoutColors, RsvpColors {
  'hotkey-background': string
  'hotkey-border': string
}

export interface ThemeColors
  extends UiColors, NoteColors, BrandColors, CalendarColors, SurfaceColors, ControlColors {
  highlight: string
  'highlight-grey': string
  primary: string
  secondary: string
  error: string
  info: string
  success: string
  warning: string
}

export interface NoteVariables {
  'note-list-background-opacity': number
  'note-details-background-opacity': number
  'note-table-background-opacity': number
  // Soft shadow under an expanded mention's peek card; 'none' in dark, where a
  // surface-text-based shadow reads as a halo on the near-black page.
  'note-mention-card-shadow': string
}

export interface ThemeVariables extends NoteVariables {
  'floating-shadow': string
  'shadow-intensity': number
  'theme-highlight-glossy': string
  'theme-overlay-multiplier': number
  'calendar-past-brightness': number
  'calendar-hover-brightness': number
  'calendar-declined-brightness': number
  'calendar-event-gradient-intensity': number
  'calendar-background-event-minimized-brightness': number
}

export interface Theme {
  dark: boolean
  colors: ThemeColors
  variables: ThemeVariables
}

export interface ThemeDefinition {
  id: string
  name: string
  type: 'light' | 'dark'
  theme: Theme
}

const clamp01 = (value: number) => {
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

const parseColorToRgb = (color: string): [number, number, number] | null => {
  const hex = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  if (hex) {
    return hex.slice(1).map((x) => parseInt(x, 16)) as [number, number, number]
  }

  const rgb = color.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i)
  if (!rgb) return null

  const channels = rgb.slice(1).map((x) => Number.parseInt(x, 10))
  if (channels.some((x) => Number.isNaN(x) || x < 0 || x > 255)) return null

  return channels as [number, number, number]
}

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255

  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const delta = max - min

  let h = 0
  const l = (max + min) / 2

  if (delta === 0) {
    return [0, 0, l]
  }

  const s = delta / (1 - Math.abs(2 * l - 1))

  switch (max) {
    case rn:
      h = ((gn - bn) / delta) % 6
      break
    case gn:
      h = (bn - rn) / delta + 2
      break
    default:
      h = (rn - gn) / delta + 4
  }

  h *= 60
  if (h < 0) h += 360

  return [h, s, l]
}

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  if (s === 0) {
    const gray = Math.round(l * 255)
    return [gray, gray, gray]
  }

  const c = (1 - Math.abs(2 * l - 1)) * s
  const hh = h / 60
  const x = c * (1 - Math.abs((hh % 2) - 1))

  let r1 = 0
  let g1 = 0
  let b1 = 0

  if (hh >= 0 && hh < 1) {
    r1 = c
    g1 = x
  } else if (hh >= 1 && hh < 2) {
    r1 = x
    g1 = c
  } else if (hh >= 2 && hh < 3) {
    g1 = c
    b1 = x
  } else if (hh >= 3 && hh < 4) {
    g1 = x
    b1 = c
  } else if (hh >= 4 && hh < 5) {
    r1 = x
    b1 = c
  } else {
    r1 = c
    b1 = x
  }

  const m = l - c / 2
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)]
}

const adjustLightness = (color: string, amount: number, direction: 'lighten' | 'darken') => {
  const rgb = parseColorToRgb(color)
  if (!rgb) return color

  const [h, s, l] = rgbToHsl(...rgb)
  const clampedAmount = clamp01(amount)
  const nextL = direction === 'lighten' ? l + (1 - l) * clampedAmount : l * (1 - clampedAmount)

  const [r, g, b] = hslToRgb(h, s, clamp01(nextL))
  return `rgb(${r}, ${g}, ${b})`
}

export const lightenColor = (color: string, amount: number) => {
  return adjustLightness(color, amount, 'lighten')
}

export const darkenColor = (color: string, amount: number) => {
  return adjustLightness(color, amount, 'darken')
}

export const mixColors = (color1: string, color2: string, amount: number) => {
  const rgb1 = color1.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  const rgb2 = color2.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  if (!rgb1 || !rgb2) return color1

  const [r1, g1, b1] = rgb1.slice(1).map((x) => parseInt(x, 16))
  const [r2, g2, b2] = rgb2.slice(1).map((x) => parseInt(x, 16))
  return `rgb(${Math.round(r1 * (1 - amount) + r2 * amount)}, ${Math.round(g1 * (1 - amount) + g2 * amount)}, ${Math.round(b1 * (1 - amount) + b2 * amount)})`
}
